// Eingehende Anrufe als kompakte Leiste (Wunsch 18.10.2026, ersetzt die dicke Karte rechts): je klingelndem Anruf eine
// Zeile in der Optik der Power-Dialer-Leiste, direkt über ihr (DialerLeiste hängt sie ein), auf jeder Seite und auch
// während der Dialer oder ein Gespräch läuft. Links Klingel, „Eingehender Anruf“ und Lead (klickbar) bzw. Nummer,
// darunter Status, Kontakt, Nummer, angerufene Nummer und Grund; rechts „Annehmen“ (grün; im Gespräch „Beenden &
// annehmen“, beendet das laufende Gespräch ohne Nachfrage), „Ablehnen“ (rot) und X = Ignorieren (nur der Klingelton
// hier ist aus, für den Anrufer klingelt es weiter; verpasst landet es danach wie gewohnt in der Inbox).
// Tastenkürzel: Enter = Annehmen, Esc = Ignorieren (wirken auf den ältesten Anruf, nie beim Tippen in einem Feld).
// Die Klassen `.tel-eingehend`, `__wer`, `__zeile`, `__nummer`, `__hinweis`, `__knopf--an` und `__knopf--ab` bleiben:
// die Desktop-App (preload.js) liest und bedient sie als Rückfall.
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "../components/Icon";
import { StatusPille } from "../components/Bausteine";
import { telefonText } from "../werkzeuge/telefon";
import { ebene, tipptGerade } from "../werkzeuge/tasten";
import { desktopEingehend, desktopEingehendEnde } from "./desktop";
import { eingehendMeldung } from "./logik";
import { useTelefonie } from "./TelefonieProvider";
import type { EingehenderAnruf } from "./typen";
import "./telefonie.css";

const leadPfad = (id: string) => `/lead/${encodeURIComponent(id)}`;

/** Schaltfläche oder Link im Fokus: Enter gehört ihr (sie wird ausgelöst), nicht dem Annehmen. */
const aufKnopf = (ziel: EventTarget | null): boolean => ["BUTTON", "A"].includes((ziel as HTMLElement | null)?.tagName ?? "");

function Zeile({ e, laeuft }: { e: EingehenderAnruf; laeuft: boolean }) {
  const { eingehendAnnehmen, eingehendAblehnen, eingehendIgnorieren } = useTelefonie();
  const navigate = useNavigate();
  const wer = e.leadName ?? e.kontaktName ?? (e.anonym ? "Anonymer Anrufer" : "Unbekannte Nummer");
  const nummer = e.nummer ? telefonText(e.nummer) : "";
  const annehmen = async () => {
    await eingehendAnnehmen(e.id);
    if (e.leadId) navigate(leadPfad(e.leadId));
  };
  return (
    <div className="tel-leiste__zeile tel-eingehend" role="alertdialog" aria-live="assertive" aria-label="Eingehender Anruf">
      <div className="tel-leiste__richtung tel-eingehend__richtung" title="Eingehender Anruf">
        <span className="tel-eingehend__glocke">
          <Icon name="glocke" groesse={18} />
        </span>
        <span>IN</span>
      </div>
      <div className="tel-leiste__wer">
        <div className="tel-leiste__namen tel-eingehend__kopf">
          <span className="tel-eingehend__titel">Eingehender Anruf</span>
          {e.leadId ? (
            <Link className="tel-eingehend__wer" to={leadPfad(e.leadId)} title="Zum Lead">
              {wer}
            </Link>
          ) : (
            <span className="tel-eingehend__wer">{wer}</span>
          )}
        </div>
        <div className="tel-eingehend__zeile">
          {e.leadStatus ? <StatusPille label={e.leadStatus} /> : null}
          {e.kontaktName && e.leadName ? <span>{e.kontaktName}</span> : null}
          {nummer ? <span className="tel-eingehend__nummer">{nummer}</span> : null}
          {e.angerufen ? (
            <span className="tel-eingehend__angerufen">
              Angerufen: {e.angerufenName ? `${e.angerufenName}, ` : ""}
              {telefonText(e.angerufen)}
            </span>
          ) : null}
          {e.mehrere ? <span className="tel-eingehend__hinweis">Die Nummer gehört zu mehreren Leads.</span> : null}
          {e.grund ? <span className="tel-eingehend__hinweis">{e.grund}</span> : null}
        </div>
      </div>
      <div className="tel-leiste__rechts tel-eingehend__knoepfe">
        <button
          type="button"
          className="tel-eingehend__knopf tel-eingehend__knopf--an"
          title={laeuft ? "Beendet das laufende Gespräch und nimmt an (Enter)" : "Annehmen (Enter)"}
          onClick={() => void annehmen()}
        >
          <Icon name="eingehend" groesse={14} />
          {laeuft ? "Beenden & annehmen" : "Annehmen"}
        </button>
        <button
          type="button"
          className="tel-eingehend__knopf tel-eingehend__knopf--ab"
          title="Ablehnen: der Anrufer geht den Ablehnen-Weg (Mailbox)"
          onClick={() => eingehendAblehnen(e.id)}
        >
          Ablehnen
        </button>
        <button
          type="button"
          className="tel-schliessen tel-eingehend__ignorieren"
          title="Ignorieren (Esc): Klingelton hier aus, für den Anrufer klingelt es weiter"
          aria-label="Ignorieren"
          onClick={() => eingehendIgnorieren(e.id)}
        >
          <Icon name="schliessen" groesse={14} />
        </button>
      </div>
    </div>
  );
}

export function EingehendLeiste() {
  const { eingehend, aktiverAnruf, eingehendAnnehmen, eingehendIgnorieren } = useTelefonie();
  const navigate = useNavigate();
  const erster = eingehend[0] ?? null;
  const ersterId = erster?.id ?? null;

  // Desktop-App: ausdrücklich melden, dass es klingelt und wann es endet (die CSS-Klassen bleiben als Rückfall: die App
  // erkennt die Leiste auch selbst, gleiche Anrufe zählt sie nur einmal).
  useEffect(() => {
    if (!erster) return;
    desktopEingehend(eingehendMeldung(erster, erster.nummer ? telefonText(erster.nummer) : ""));
    return () => desktopEingehendEnde();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ersterId]);

  // Kürzel: Enter = Annehmen, Esc = Ignorieren (ältester Anruf)
  useEffect(() => {
    if (!ersterId) return;
    const taste = (ev: KeyboardEvent) => {
      if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey || ev.isComposing || tipptGerade(ev) || ebene.offen()) return;
      // Offener Dialog oder offenes Menü: Enter und Esc gehören ihm
      if (document.querySelector('[role="dialog"], [role="menu"], [aria-modal="true"]')) return;
      if (ev.key === "Enter" && !aufKnopf(ev.target)) {
        ev.preventDefault();
        const leadId = erster?.leadId;
        void eingehendAnnehmen(ersterId).then(() => {
          if (leadId) navigate(leadPfad(leadId));
        });
      } else if (ev.key === "Escape") {
        // vor den Seitenkürzeln (Capture): Esc beendet nur das Klingeln, sonst nichts
        ev.preventDefault();
        ev.stopPropagation();
        eingehendIgnorieren(ersterId);
      }
    };
    window.addEventListener("keydown", taste, true);
    return () => window.removeEventListener("keydown", taste, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ersterId, eingehendAnnehmen, eingehendIgnorieren, navigate]);

  if (eingehend.length === 0) return null;
  return (
    <section className="tel-leiste tel-eingehendleiste" aria-label="Eingehende Anrufe" data-tel-eingehend="">
      {eingehend.map((e) => (
        <Zeile key={e.id} e={e} laeuft={!!aktiverAnruf} />
      ))}
    </section>
  );
}
